import { chatApi } from '@/lib';
import { ChatRequest, ChatResponse, StreamChunk } from '../types';
import { appNotify } from '@/lib/appNotify';
import { configSettings } from '@/lib/config';
import CryptoJS from 'crypto-js';

export const sendMessage = async (message: string) => {
    const response = await chatApi.post<ChatResponse>('/v1/chat', {
        content: message,
    });
    return response.data;
};

export const sendStreamingMessage = async (
    message: string,
    onChunk: (chunk: StreamChunk) => void,
    conversationId?: string,
    promptName?: string
): Promise<void> => {
    const requestBody: ChatRequest = {
        message,
        conversation_id: conversationId,
        prompt_name: promptName,
    };

    try {
        const response = await chatApi.post('/v1/chat', requestBody, {
            responseType: 'stream',
            adapter: 'fetch',
            timeout: configSettings.isDevelopment ? 0 : 10000, // 0 = no timeout in dev, 10s in prod
        });

        const reader = response.data.getReader();
        if (!reader) {
            throw new Error('Response body is not readable');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        try {
            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    // Process any remaining content in the buffer
                    if (buffer.trim()) {
                        try {
                            const chunk: StreamChunk = JSON.parse(buffer.trim());
                            onChunk(chunk);
                        } catch (parseError) {
                            console.error('Error parsing final chunk:', parseError, 'Buffer:', buffer);
                        }
                    }
                    break;
                }

                // Decode the chunk and add to buffer
                const chunkText = decoder.decode(value, { stream: true });
                buffer += chunkText;

                // Split buffer by newlines to get complete JSON lines
                const lines = buffer.split('\n');

                // Keep the last line in the buffer (might be incomplete)
                buffer = lines.pop() || '';

                // Process all complete lines
                for (const line of lines) {
                    const trimmedLine = line.trim();
                    if (trimmedLine) {
                        try {
                            const chunk: StreamChunk = JSON.parse(trimmedLine);
                            onChunk(chunk);

                            // Break early if we receive a 'done' chunk
                            if (chunk.type === 'done') {
                                return;
                            }
                        } catch (parseError) {
                            console.error('Error parsing chunk:', parseError, 'Line:', trimmedLine);
                        }
                    }
                }
            }
        } finally {
            reader.releaseLock();
        }
    } catch (error) {
        console.error('Streaming error:', error);
        onChunk({
            type: 'error',
            error: error instanceof Error ? error.message : 'Unknown streaming error',
        });
    }
};

export const rateMessage = async (messageId: string, messageContent: string, rating: number) => {
    const conversationId = messageId.split('-').slice(0, -1).join('-');
    const messageEntity = CryptoJS.MD5(messageContent).toString();

    const response = await chatApi.post<ChatResponse>(`/v1/chat/message/rating`, {
        conversation_id: conversationId,
        message_entity: messageEntity,
        rating,
    });
    return response.data;
};

export const copyMessage = (content: string, index: string, onCopy: (index: string | null) => void) => {
    navigator.clipboard
        .writeText(content)
        .then(() => {
            onCopy(index);

            setTimeout(() => {
                onCopy(null);
            }, 3000);
        })
        .catch(err => {
            console.error('Error copying message:', err);
            appNotify({
                type: 'error',
                text: 'Error copying message',
            });
        });
};
